// Keeps a panel's OCR status connected to the background job, across navigation.
//
// OCR runs server-side (see _start_ocr_job in backend), so its status must not
// live only in component state — otherwise leaving the Exam / Model Solution
// view and coming back loses the running status even though the job continues.
// On (re)mount this hook checks /ocr_status and, if a job for *this* document
// (`kind`) is still running, resumes the status line + polling until it
// finishes. `startWatch()` is called right after the Run OCR POST to begin
// watching the job it just started. Polling is tied to an AbortController that
// fires on unmount, so navigating away stops the poll instead of leaking it.

import { useEffect, useRef } from "react";

import { apiJson } from "../api/client";
import { pollOcrJob, type OcrStatus } from "./ocrProgress";

export interface OcrWatchLabels {
  progress: (page: number, total: number) => string;
  done: (pages: number) => string;
  failed: string;
}

export function useOcrWatch(opts: {
  projectId: number;
  kind: "exam" | "model_full";
  setStatus: (s: string | null) => void;
  setRunning: (b: boolean) => void;
  labels: OcrWatchLabels;
  onDone?: () => void | Promise<void>;
}): { startWatch: () => void } {
  const { projectId, kind, setStatus, setRunning, labels, onDone } = opts;
  const abortRef = useRef<AbortController | null>(null);

  async function watch(signal: AbortSignal, initial?: OcrStatus) {
    setRunning(true);
    if (initial?.total) setStatus(labels.progress(initial.page ?? 0, initial.total));
    const final = await pollOcrJob(
      projectId,
      (s) => {
        if (s.total) setStatus(labels.progress(s.page ?? 0, s.total));
      },
      1000,
      signal,
    );
    if (!final) return; // aborted (panel unmounted / project switched)
    if (final.state === "error") {
      setStatus(labels.failed);
    } else {
      await onDone?.();
      setStatus(labels.done(final.pages ?? 0));
    }
    setRunning(false);
  }

  useEffect(() => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    void (async () => {
      let s: OcrStatus | null = null;
      try {
        s = await apiJson<OcrStatus>(`/api/projects/${projectId}/ocr_status`);
      } catch {
        return;
      }
      if (ctrl.signal.aborted || s.state !== "running" || s.kind !== kind) return;
      await watch(ctrl.signal, s);
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, kind]);

  return {
    startWatch() {
      const ctrl = abortRef.current;
      if (ctrl) void watch(ctrl.signal);
    },
  };
}
